import { X } from "lucide-react";
import type { ButtonHTMLAttributes } from "react";
import { cn } from "../lib/cn";

export interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Toggle state for filter chips. */
  selected?: boolean;
  /** Show a remove affordance (used for picked values). */
  onRemove?: () => void;
  mono?: boolean;
}

/** Toggleable filter chip, or a removable value chip when `onRemove` is given. */
export function Chip({ selected, onRemove, mono, className, children, ...props }: ChipProps) {
  const base = cn(
    "inline-flex h-[26px] items-center gap-1.5 rounded-sm border px-2.5 text-sm font-medium transition-colors",
    selected
      ? "border-accent bg-accent-subtle text-accent-fg"
      : "border-border bg-surface text-fg-muted hover:bg-hover hover:text-fg",
    mono && "font-mono text-xs",
    className,
  );
  if (onRemove) {
    return (
      <span className={cn(base, "rounded-pill border-border bg-surface-subtle text-fg")}>
        {children}
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove"
          className="rounded-xs text-fg-subtle hover:text-fg"
        >
          <X className="size-3" aria-hidden />
        </button>
      </span>
    );
  }
  return (
    <button type="button" aria-pressed={selected} className={base} {...props}>
      {children}
    </button>
  );
}

export type BadgeTone = "neutral" | "accent" | "ok" | "warn" | "err";

const badgeTone: Record<BadgeTone, string> = {
  neutral: "bg-surface-inset text-fg-muted",
  accent: "bg-accent-subtle text-accent-fg",
  ok: "bg-ok-subtle text-ok-fg",
  warn: "bg-warn-subtle text-warn-fg",
  err: "bg-err-subtle text-err-fg",
};

/** Small uppercase label: OVERRIDDEN, NEW, FLAKY, MUTED. Not for status; use StatusBadge for that. */
export function Badge({
  tone = "neutral",
  className,
  children,
}: {
  tone?: BadgeTone;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-3.5 items-center rounded-xs px-1.5 text-2xs font-semibold tracking-wider uppercase",
        badgeTone[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * Neutral pill for metadata: `table`, `marts`, `tag: nightly`, a repository, a warehouse. Mono
 * when the content is an identifier. Tinted only when it carries state (ok / warn / err).
 */
export function MetaChip({
  children,
  icon,
  mono = false,
  tone = "neutral",
  filled = false,
  className,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  mono?: boolean;
  tone?: BadgeTone;
  /** Tinted fill with no border: the entity chips on a model header. */
  filled?: boolean;
  className?: string;
}) {
  const fill =
    tone === "neutral"
      ? filled
        ? "bg-surface-inset text-fg-muted"
        : "border border-border bg-surface text-fg-muted"
      : badgeTone[tone];
  return (
    <span
      className={cn(
        "inline-flex h-6 items-center gap-1.5 rounded-pill px-2.5 text-xs font-semibold",
        mono && "font-mono font-medium",
        fill,
        className,
      )}
    >
      {icon ? (
        <span className="[&>svg]:size-3" aria-hidden>
          {icon}
        </span>
      ) : null}
      {children}
    </span>
  );
}

/** The schedule line under a job: mono cron, then the timezone. */
export function CronChip({
  expr,
  tz = "UTC",
  className,
}: {
  expr: string;
  tz?: string;
  className?: string;
}) {
  return (
    <span
      className={cn("inline-flex items-center gap-1.5 font-mono text-xs text-fg-subtle", className)}
    >
      <span>{expr}</span>
      <span aria-hidden>·</span>
      <span>{tz}</span>
    </span>
  );
}
